import { useEffect, useMemo, useRef } from "react";
import { polygonArea } from "../geometry/fov";
import { COLOR_WITHIN_FOV, COLOR_OUT_OF_FOV } from "../constants/GeometryFOVConstants";
import "./CanvasPane.css";

const COLOR_INCLINED = "#f97362";
const COLOR_COPLANAR = "#5EEAD4";
const COLOR_GIMBAL = "#F5A623";
const COLOR_BORDER = "#4c4c4c";
const COLOR_AXIS_HORIZONTAL = "#5d7f2d";
const COLOR_AXIS_VERTICAL = "#7a3d45";
const COLOR_VOLUME_EDGE = "#046AB2";
const COLOR_VOLUME_SURFACE = "#25ADDE";
const COLOR_FG = "#efefef";

// A setup has zero rotational FOV loss when its single-angle footprint area
// already equals the all-angle intersection area (footprints don't shrink
// the overlap at all, e.g. computePlanarGeom's fixed world-frame detector).
function isRotationInvariant(result) {
  if (!result.footprints.length || result.area <= 0) return false;
  const singleArea = polygonArea(result.footprints[0]);
  return Math.abs(singleArea - result.area) / result.area < 1e-6;
}

export default function FovCanvas({
  fovResults,
  gridHalf,
  showInclined,
  showCoplanar,
  showGimbal,
  showAllFootprints,
  volRes,
  volX,
  volY,
  offsetVolX = 0,
  offsetVolY = 0,
  showVolume,
  withinFov = true,
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  const invariance = useMemo(
    () => ({
      inclined: isRotationInvariant(fovResults.inclined),
      coplanar: isRotationInvariant(fovResults.coplanar),
      gimbal: isRotationInvariant(fovResults.gimbal),
    }),
    [fovResults]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.fillStyle = "#373737";
      ctx.fillRect(0, 0, w, h);

      const margin = 40;
      const size = Math.min(w, h) - margin * 2;
      const cx = w / 2;
      const cy = h / 2;
      const scale = size / (2 * gridHalf);

      const toPx = ([x, y]) => [cx + x * scale, cy - y * scale];

      // grid
      ctx.strokeStyle = COLOR_BORDER;
      ctx.lineWidth = 1;
      const gridLines = 8;
      for (let i = 0; i <= gridLines; i++) {
        const gx = -gridHalf + (2 * gridHalf * i) / gridLines;
        const [px] = toPx([gx, 0]);
        ctx.beginPath();
        ctx.moveTo(px, cy - size / 2);
        ctx.lineTo(px, cy + size / 2);
        ctx.stroke();
        const [, py] = toPx([0, gx]);
        ctx.beginPath();
        ctx.moveTo(cx - size / 2, py);
        ctx.lineTo(cx + size / 2, py);
        ctx.stroke();
      }

      // axes
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = COLOR_AXIS_HORIZONTAL;
      ctx.beginPath();
      ctx.moveTo(cx - size / 2, cy);
      ctx.lineTo(cx + size / 2, cy);
      ctx.stroke();
      ctx.strokeStyle = COLOR_AXIS_VERTICAL;
      ctx.beginPath();
      ctx.moveTo(cx, cy - size / 2);
      ctx.lineTo(cx, cy + size / 2);
      ctx.stroke();

      const drawFootprints = (footprints, color, alpha) => {
        ctx.strokeStyle = color;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 0.6;
        for (let i = 0; i < footprints.length; i++) {
          const poly = footprints[i];
          ctx.beginPath();
          poly.forEach((pt, idx) => {
            const [px, py] = toPx(pt);
            if (idx === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          });
          ctx.closePath();
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      };

      if (showAllFootprints) {
        if (showInclined && fovResults.inclined.footprints.length) {
          drawFootprints(fovResults.inclined.footprints, COLOR_INCLINED, 0.12);
        }
        if (showCoplanar && fovResults.coplanar.footprints.length) {
          drawFootprints(fovResults.coplanar.footprints, COLOR_COPLANAR, 0.12);
        }
        if (showGimbal && fovResults.gimbal.footprints.length) {
          drawFootprints(fovResults.gimbal.footprints, COLOR_GIMBAL, 0.12);
        }
      }

      const drawOutline = (outline, color) => {
        if (outline.length < 3) return;
        ctx.beginPath();
        outline.forEach((pt, idx) => {
          const [px, py] = toPx(pt);
          if (idx === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.closePath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.2;
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.15;
        ctx.fill();
        ctx.globalAlpha = 1;
      };

      if (showInclined) drawOutline(fovResults.inclined.outline, COLOR_INCLINED);
      if (showCoplanar) drawOutline(fovResults.coplanar.outline, COLOR_COPLANAR);
      if (showGimbal) drawOutline(fovResults.gimbal.outline, COLOR_GIMBAL);

      // Volume footprint: top-down (X/Y) extent of the reconstruction cuboid,
      // volRes (mm/px) * volX/volY (px), centered at (offsetVolX, offsetVolY).
      if (showVolume && volRes > 0 && volX > 0 && volY > 0) {
        const halfSizeX = (volRes * volX) / 2;
        const halfSizeY = (volRes * volY) / 2;
        const corners = [
          [offsetVolX - halfSizeX, offsetVolY - halfSizeY],
          [offsetVolX + halfSizeX, offsetVolY - halfSizeY],
          [offsetVolX + halfSizeX, offsetVolY + halfSizeY],
          [offsetVolX - halfSizeX, offsetVolY + halfSizeY],
        ];
        ctx.beginPath();
        corners.forEach((pt, idx) => {
          const [px, py] = toPx(pt);
          if (idx === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.closePath();
        ctx.fillStyle = COLOR_VOLUME_SURFACE;
        ctx.globalAlpha = 0.5;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = COLOR_VOLUME_EDGE;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // origin marker
      ctx.strokeStyle = COLOR_FG;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - 6, cy);
      ctx.lineTo(cx + 6, cy);
      ctx.moveTo(cx, cy - 6);
      ctx.lineTo(cx, cy + 6);
      ctx.stroke();
    };

    draw();
    const resizeObserver = new ResizeObserver(draw);
    resizeObserver.observe(wrap);
    return () => resizeObserver.disconnect();
  }, [
    fovResults,
    gridHalf,
    showInclined,
    showCoplanar,
    showGimbal,
    showAllFootprints,
    volRes,
    volX,
    volY,
    offsetVolX,
    offsetVolY,
    showVolume,
  ]);

  return (
    <div className="canvas-mount" ref={wrapRef}>
      <canvas ref={canvasRef} />
      <div className="canvas-label">FOV</div>
      <div className="canvas-legend">
        <span className="legend-item"><span className="legend-dot" style={{ background: COLOR_INCLINED }} /> Inclined</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: COLOR_COPLANAR }} /> Coplanar</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: COLOR_GIMBAL }} /> Gimbal Lock</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: COLOR_VOLUME_SURFACE }} /> Volume</span>
      </div>
      <div className="canvas-fov-status" style={{ color: withinFov ? COLOR_WITHIN_FOV : COLOR_OUT_OF_FOV }}>
        {withinFov ? "Within FOV" : "Out of FOV"}
      </div>
      {showAllFootprints && (invariance.inclined || invariance.coplanar || invariance.gimbal) && (
        <div className="canvas-footer">
          <div className="canvas-note">
            {invariance.inclined && showInclined && "Inclined footprint is rotation-invariant — no FOV loss. "}
            {invariance.coplanar && showCoplanar && "Coplanar footprint is rotation-invariant — no FOV loss. "}
            {invariance.gimbal && showGimbal && "Gimbal lock footprint is rotation-invariant — no FOV loss."}
          </div>
        </div>
      )}
    </div>
  );
}
